import { Box, Button } from "@mui/material";
import { COVERAGE_LABELS } from "@platform/census-components";
import { Alert } from "@platform/design-system";
import { useMemo } from "react";
import { useMatch, useNavigate } from "react-router";

import { ROUTE_NAMES } from "../../constants";

import { RfpData } from "./types";
import {
  CoverageValidationResult,
  validateCoverageFields,
} from "./validateCoverageFields";

const COVERAGE_REQUIRED_FIELDS = {
  POLICY_PERIOD: "benefitPlans.policyPeriod",
  OPTION_NAME: "benefitPlans.optionName",
  EMPLOYER_CONTRIBUTION: "benefitPlans.benefits.employerContribution",
  BENEFIT_PLAN_TYPE: "benefitPlans.type",
  BENEFIT_AMOUNT_TYPE: "benefitPlans.benefits.benefitAmountType",
  BENEFIT_DESCRIPTION_EMPLOYEE_TYPE:
    "benefitPlans.benefits.benefitDescriptionEmployeeType",
  ADND_INCLUDED: "benefitPlans.benefits.adndIncluded",
};

type Props = {
  rfpId?: string;
  onClose: VoidFunction;
  coverageValidations: CoverageValidationResult;
};
function CensusTabCoverageValidations({
  rfpId,
  onClose,
  coverageValidations,
}: Props): React.JSX.Element {
  const navigate = useNavigate();
  const matches = useMatch(
    `/${ROUTE_NAMES.RFPS}/:id/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}`,
  );

  return (
    <Box px={3} pt={2}>
      <Alert
        severity="error"
        variant="outlined"
        action={
          rfpId ? (
            <Box width="max-content">
              {/* TODO(ds-button): needs manual review — outlined error has no DS variant (danger is contained only) */}
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                  onClose();

                  // If already on the Build 2.0 page, do not navigate
                  if (matches) {
                    return;
                  }
                  navigate(
                    `/${ROUTE_NAMES.RFPS}/${rfpId}/${ROUTE_NAMES.RFP_WORKBENCH_BUILD_2}`,
                  );
                }}
              >
                Edit Coverage
              </Button>
            </Box>
          ) : undefined
        }
        sx={{ mb: 2 }}
      >
        One or more of the coverages is missing details that prevent the census
        from successfully being processed. Please address the following coverage
        plans:
        <Box component="ul" sx={{ pl: 4, my: 0 }}>
          {coverageValidations.validations.map((validation) => (
            <li key={validation.coverageId}>
              <strong>
                {COVERAGE_LABELS[validation.coverageType] ??
                  validation.coverageType}
              </strong>
              <Box component="ul" sx={{ pl: 4, my: 0 }}>
                {validation.benefitPlans.map((plan) => (
                  <li key={plan.planId} style={{ listStyleType: "circle" }}>
                    <span>
                      {plan.planIdentifier ? `${plan.planIdentifier}: ` : ""}
                      {plan.missingFields.join(", ")}
                    </span>
                  </li>
                ))}
              </Box>
            </li>
          ))}
        </Box>
      </Alert>
    </Box>
  );
}

const CONFIG = {
  dental: {
    requiredFields: [
      COVERAGE_REQUIRED_FIELDS.POLICY_PERIOD,
      COVERAGE_REQUIRED_FIELDS.OPTION_NAME,
      COVERAGE_REQUIRED_FIELDS.BENEFIT_PLAN_TYPE,
    ],
  },
  vision: {
    requiredFields: [
      COVERAGE_REQUIRED_FIELDS.POLICY_PERIOD,
      COVERAGE_REQUIRED_FIELDS.OPTION_NAME,
    ],
  },
  std: {
    requiredFields: [
      COVERAGE_REQUIRED_FIELDS.POLICY_PERIOD,
      COVERAGE_REQUIRED_FIELDS.EMPLOYER_CONTRIBUTION,
    ],
  },
  ltd: {
    requiredFields: [
      COVERAGE_REQUIRED_FIELDS.POLICY_PERIOD,
      COVERAGE_REQUIRED_FIELDS.EMPLOYER_CONTRIBUTION,
    ],
  },
  basicLifeAdnd: {
    requiredFields: [
      COVERAGE_REQUIRED_FIELDS.POLICY_PERIOD,
      COVERAGE_REQUIRED_FIELDS.EMPLOYER_CONTRIBUTION,
      {
        fieldPath: COVERAGE_REQUIRED_FIELDS.BENEFIT_AMOUNT_TYPE,
        name: "Benefit Amount",
      },
    ],
  },
  voluntaryLifeAdnd: {
    requiredFields: [
      COVERAGE_REQUIRED_FIELDS.POLICY_PERIOD,
      COVERAGE_REQUIRED_FIELDS.ADND_INCLUDED,
      {
        fieldPath: COVERAGE_REQUIRED_FIELDS.BENEFIT_DESCRIPTION_EMPLOYEE_TYPE,
        name: "Benefit Description for Employee",
      },
    ],
  },
};

const useCoverageValidations = (rfp: RfpData): CoverageValidationResult =>
  useMemo(() => {
    if (!rfp) {
      return { hasErrors: false, validations: [] };
    }

    return validateCoverageFields(rfp, CONFIG);
  }, [rfp]);

export { CensusTabCoverageValidations, useCoverageValidations };
